import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

// Tokens first, then the component classes that read them, then our own screen styles.
import './styles/fonts.css';
import './styles/tokens.css';
import './styles/components.css';
import './styles/app.css';
import { storedTokens } from './api/client';
import { registerServiceWorker } from './offline/worker';
import { startTheme } from './theme';
import { Login } from './screens/Login';
import { Shell } from './screens/Shell';
import { Categories } from './screens/Categories';
import { Questions } from './screens/Questions';
import { Templates } from './screens/Templates';
import { TemplateEditor } from './screens/TemplateEditor';
import { VisitTypes } from './screens/VisitTypes';
import { Locations } from './screens/Locations';
import {
  ActionCompanies,
  CentralTeams,
  ChecklistTitles,
  ClosedDays,
  ScoreBandsScreen,
} from './screens/Parameters';
import { SystemParameters } from './screens/SystemParameters';
import { Audit } from './screens/Audit';
import { Actions } from './screens/Actions';
import { Audits } from './screens/Audits';
import { VisitResults } from './screens/VisitResults';
import { FollowUp } from './screens/FollowUp';
import { MailSettings } from './screens/MailSettings';
import { LocationMap } from './screens/LocationMap';
import { Tasks } from './screens/Tasks';
import { AuditPlans } from './screens/AuditPlans';
import { MapSettings } from './screens/MapSettings';
import { Chart } from './screens/Chart';
import { Professions } from './screens/Professions';
import { Roles } from './screens/Roles';
import { UserGroups } from './screens/UserGroups';
import { Users } from './screens/Users';
import { allowed, session } from './auth/session';
import { landingPath } from './layout/navigation';
import {
  Cities,
  Countries,
  Departments,
  Districts,
  LocationTypes,
  Operations,
  Regions,
} from './screens/Organisation';

/**
 * Guards a route by whether there is a session at all.
 *
 * Deliberately not a permission check. The token is the client's copy of a claim the server
 * made, and a client that decides for itself what it may do is a client that can be argued
 * with. Every endpoint behind this requires the administrator role server-side; this only
 * keeps a signed-out person from staring at an empty table wondering what broke.
 */
function RequireSession({ children }: { children: React.ReactNode }) {
  return storedTokens() ? <>{children}</> : <Navigate to="/giris" replace />;
}

/**
 * "/" sends somebody to the first screen they can actually use.
 *
 * <p>Not a fixed address: a quality manager holds no `audits.conduct` and used to land on
 * Denetimler, whose "start an audit" call answers 403 — so the first thing they saw after
 * signing in was an error. The answer comes off the same list the menu is built from, so a
 * screen added to the menu is a screen this can land on without anybody remembering to
 * update it.</p>
 */
function Landing() {
  return <Navigate to={landingPath((permission) => allowed(session(), permission))} replace />;
}

// Before the first paint, so a device set to dark never flashes white.
startTheme();

/**
 * Whether a new version is waiting, and what to do about it.
 *
 * <p>Kept outside React because it is answered before React has rendered anything, and the
 * one component that shows it subscribes. A store this small does not need a library.</p>
 */
let acceptUpdate: (() => void) | null = null;
const updateWatchers = new Set<() => void>();

export function updateReady(): (() => void) | null {
  return acceptUpdate;
}

export function watchForUpdate(onChange: () => void): () => void {
  updateWatchers.add(onChange);
  return () => updateWatchers.delete(onChange);
}

registerServiceWorker((accept) => {
  acceptUpdate = accept;
  updateWatchers.forEach((notify) => notify());
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/giris" element={<Login />} />
        <Route
          path="/"
          element={
            <RequireSession>
              <Shell />
            </RequireSession>
          }
        >
          <Route index element={<Landing />} />
          <Route path="ziyaret-tipleri" element={<VisitTypes />} />
          <Route path="kategoriler" element={<Categories />} />
          <Route path="sorular" element={<Questions />} />
          <Route path="basliklar" element={<ChecklistTitles />} />
          <Route path="ziyaret-skorlari" element={<ScoreBandsScreen />} />
          <Route path="aksiyon-firmalari" element={<ActionCompanies />} />
          <Route path="merkez-ekipleri" element={<CentralTeams />} />
          <Route path="lokasyon-tipleri" element={<LocationTypes />} />
          <Route path="sistem-parametreleri" element={<SystemParameters />} />
          <Route path="departmanlar" element={<Departments />} />
          <Route path="operasyonlar" element={<Operations />} />
          <Route path="bolgeler" element={<Regions />} />
          <Route path="lokasyonlar" element={<Locations />} />
          <Route path="kapali-gunler" element={<ClosedDays />} />
          <Route path="ulkeler" element={<Countries />} />
          <Route path="sehirler" element={<Cities />} />
          <Route path="ilceler" element={<Districts />} />
          <Route path="denetimler" element={<Audits />} />
          <Route path="aksiyonlar" element={<Actions />} />
          <Route path="ziyaret-sonuclari" element={<VisitResults />} />
          <Route path="takip" element={<FollowUp />} />
          <Route path="mail-ayarlari" element={<MailSettings />} />
          <Route path="gorevlerim" element={<Tasks />} />
          <Route path="denetim-plani" element={<AuditPlans />} />
          <Route path="harita" element={<LocationMap />} />
          <Route path="harita-gostergeleri" element={<MapSettings />} />
          <Route path="denetimler/:id" element={<Audit />} />
          <Route path="kullanicilar" element={<Users />} />
          <Route path="roller" element={<Roles />} />
          <Route path="meslekler" element={<Professions />} />
          <Route path="organizasyon-semasi" element={<Chart />} />
          <Route path="kullanici-gruplari" element={<UserGroups />} />
          <Route path="sablonlar" element={<Templates />} />
          <Route path="sablonlar/:id" element={<TemplateEditor />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
);
